import {lazy, Suspense, useCallback, useEffect, useMemo, useState} from 'react';
import {
  Box,
  Button,
  Flex,
  Grid,
  HStack,
  IconButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {AnimatePresence, motion, useReducedMotion} from 'framer-motion';
import {
  TablerEdit,
  TablerDotsVertical,
  TablerTrash,
  TablerX,
  TablerChevronRight,
} from '../../icons';
import {
  displayLanguageName,
  isNonEnglish,
  languageFlag,
} from '../../constants/designTokens';
import DetailBackButton from '../common/DetailBackButton';
import {useUser} from '../../contexts/UserContext';
import {patientDisplayNameFromRecord} from '../../utils/patientNames';
import {patientService} from '../../services/patientService';
import {
  fetchEncounterMapForGrouping,
  fetchSiblingConversationsForEncounters,
  softDeleteEncounter,
} from '../../services/encounterService';
import {
  groupConversationsIntoEncounters,
  isImplicitEncounterId,
  pickRepresentativeConversation,
  toJsDate,
} from '../../services/encounterUtils';
import ViewLoader from '../common/ViewLoader';
import MetaPill from '../common/MetaPill';
import {
  buildEncounterComposerSeedFromEvent,
  clean,
  plannerPatientToPatientItem,
} from './plannerModels';
import {
  patientLanguage as patientLanguageFromItem,
  patientPhone as patientPhoneFromItem,
} from './createSessionFormUtils';
import {
  CreateSessionFooter,
  InterpreterSection,
  LocationSection,
} from './CreateSessionFormSections';
import CreateSessionScheduleModal from './CreateSessionScheduleModal';
import {
  formatPlannerSidebarTime,
  groupPlannerRowsByDate,
  plannerLanguageMarker,
  PLANNER_OPALITE_SELECTION_RADIUS,
  PLANNER_POINTER_HOVER_TRANSITION,
  PlannerNavigationSectionHeader,
} from './PlannerSidebarList';
import useEncounterLauncher from './useEncounterLauncher';
import usePlannerStyles, {useScrollbarSx} from './usePlannerStyles';
import {
  buildPlannerPatientHeader,
  findScheduledEncounterEvent,
} from './plannerPatientPanelHelpers';
import PatientFormModal from '../common/PatientFormModal';
import usePatientCrud, {
  mergePlannerPatientUpdate,
} from '../common/usePatientCrud';

const SessionDetailPanel = lazy(() => import('../session/SessionDetailPanel'));

const MotionBox = motion(Box);

// Drill-in / pop-back motion for the encounter detail overlay. Matches the
// planner's right-panel easing + duration so opening an encounter and tapping
// "Back to history" feel like one continuous push/pop rather than the detail
// snapping in and vanishing in a single frame. The overlay slides off to the
// right while fading, revealing the (already-present) history list beneath it.
const ENCOUNTER_DETAIL_MOTION_EASE = [0.22, 1, 0.36, 1];
const ENCOUNTER_DETAIL_MOTION_DURATION_S = 0.26;
const ENCOUNTER_DETAIL_SLIDE_PX = 24;

const ENCOUNTER_DETAIL_OVERLAY_VARIANTS = {
  hidden: {opacity: 0, x: ENCOUNTER_DETAIL_SLIDE_PX},
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: ENCOUNTER_DETAIL_MOTION_DURATION_S,
      ease: ENCOUNTER_DETAIL_MOTION_EASE,
    },
  },
  exit: {
    opacity: 0,
    x: ENCOUNTER_DETAIL_SLIDE_PX,
    transition: {
      duration: ENCOUNTER_DETAIL_MOTION_DURATION_S,
      ease: ENCOUNTER_DETAIL_MOTION_EASE,
    },
  },
};

function normalizePatientItem(patientItem: any) {
  if (!patientItem) return null;
  const isUnified = patientItem.matchKind != null;
  const localPatient = isUnified ? patientItem.localPatient || {} : patientItem;
  const ehrPatient = isUnified ? patientItem.ehrPatient || {} : {};

  return {
    id: localPatient.id || ehrPatient.id || patientItem.id || null,
    _hasLocalRecord: Boolean(localPatient.id),
    _isEhrOnly: isUnified ? !patientItem.localPatient : false,
    _ehrPatientId: ehrPatient.id || null,
    _linkedEhrId: localPatient.fhir_patient_id || null,
  };
}

function formatEncounterDate(value: any) {
  const date = toJsDate(value);
  if (!date) return '';
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const time = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
  if (date.toDateString() === now.toDateString()) return `Today at ${time}`;
  if (date.toDateString() === yesterday.toDateString())
    return `Yesterday at ${time}`;
  return `${date.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})} at ${time}`;
}

function usePlannerPatientEncounters({
  patientItem,
  refreshKey = 0,
}: Record<string, any>) {
  const {userData} = useUser();
  // Key the timeline load on the patient's stable IDENTITY, not the
  // patientItem object. `patientItem` is rebuilt from the schedule sections,
  // and sections rebuild whenever any fetch lands in the planner's hydration
  // cache — including this hook's own timeline fetches. Depending on object
  // identity therefore cancel-and-restarts the load on every render pass,
  // which in prod (2026-07-06) looped the patient panel at ~300 req/min and
  // tripped the Cloud Armor per-IP rate ban, 429-ing the whole app.
  const normalized = normalizePatientItem(patientItem);
  const patientKey = normalized
    ? [
        normalized.id || '',
        normalized._ehrPatientId || '',
        normalized._linkedEhrId || '',
        normalized._isEhrOnly ? 'ehr-only' : '',
        normalized._hasLocalRecord ? 'local' : '',
      ].join('|')
    : '';
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const patient = useMemo(() => normalized, [patientKey]);
  const [sessions, setSessions] = useState<any[]>([]);
  const [encounterDocs, setEncounterDocs] = useState(new Map<any, any>());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<any>(null);

  useEffect(() => {
    if (!patient || !userData) {
      setSessions([]);
      setEncounterDocs(new Map());
      setIsLoading(false);
      setError(null);
      return undefined;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setSessions([]);
    setEncounterDocs(new Map());

    (async () => {
      try {
        // Schema V2: the patient timeline is the encounter list from
        // GET /api/v1/patients/:id/encounters (plus the legacy ehr_patient_id
        // arm for EHR-only patients), each row carrying its member-session
        // summaries. The old per-user `Conversation.patient_id ==` query
        // (getPatientSessionsUnified) is retired along with the
        // per-conversation patient stamp it depended on.
        const ehrPatientId =
          patient._ehrPatientId || patient._linkedEhrId || null;
        const map = await fetchEncounterMapForGrouping({
          conversations: [],
          patientId: patient._isEhrOnly ? null : patient.id,
          ehrPatientId,
          companyId: userData.companyId || null,
          // Session privacy: private encounters are dropped server-side for
          // non-contributors on the V2 read; the viewer threads through to
          // the legacy EHR-only arm's client-side filter.
          viewer: {userId: userData.userId, role: userData.role},
        });
        if (cancelled) return;

        let mergedSessions: any[] = [];
        try {
          const siblings = await fetchSiblingConversationsForEncounters(
            Array.from(map.values()),
          );
          mergedSessions = (siblings || []).filter(
            (session: any) => session.is_removed !== true,
          );
        } catch (err) {
          console.warn(
            '[PlannerPatientPanel] sibling fetch failed',
            err?.message,
          );
        }

        if (cancelled) return;
        setSessions(mergedSessions);
        setEncounterDocs(map);
      } catch (err) {
        if (cancelled) return;
        setSessions([]);
        setEncounterDocs(new Map());
        setError(err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // The effect reads only userData's identity fields; depending on the
    // context OBJECT would re-run the load (and re-cancel in-flight fetches)
    // on unrelated user-context re-renders — same churn class as patientKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    patient,
    userData?.userId,
    userData?.companyId,
    userData?.role,
    refreshKey,
  ]);

  const encounterViews = useMemo(
    () => groupConversationsIntoEncounters(sessions, encounterDocs),
    [sessions, encounterDocs],
  );

  return {
    encounterViews,
    error,
    isLoading,
  };
}

const PlannerPatientHeader = ({
  event,
  canDeletePatient,
  canEditPatient,
  onBack,
  onClose,
  onDeletePatient,
  onEditPatient,
}: Record<string, any>) => {
  const header = buildPlannerPatientHeader(event);
  const styles = usePlannerStyles();
  // When `onBack` is wired the panel is showing an encounter's detail, so the
  // header turns into a "drilled-in" view: a prominent back affordance sits in
  // the upper left (the way out), and the panel-dismiss X is hidden so the only
  // obvious exit is "go back to this patient's history".
  const inDetail = Boolean(onBack);

  return (
    <Box as="header" pb={4} data-testid="planner-patient-header">
      {inDetail && (
        <DetailBackButton
          label="Back to history"
          onClick={onBack}
          testId="planner-encounter-back-button"
          mb={3}
        />
      )}
      <Flex align="flex-start" justify="space-between" gap={4} minW={0}>
        <Box minW={0}>
          <Text
            as="h1"
            fontSize={{base: 'xl', md: '2xl'}}
            lineHeight="1.2"
            fontWeight="bold"
            sx={{fontWeight: '700 !important'}}
          >
            {header.name}
          </Text>
          {header.contextText && (
            <Text fontSize="md" lineHeight="1.4" mt={1} fontWeight="normal">
              {header.contextText}
            </Text>
          )}
        </Box>
        <HStack spacing={2} flexShrink={0} flexWrap="wrap" justify="flex-end">
          {canEditPatient && (
            <Menu placement="bottom-end">
              <MenuButton
                as={IconButton}
                type="button"
                aria-label="Patient actions"
                icon={<TablerDotsVertical size={18} aria-hidden="true" />}
                variant="ghost"
                minW="44px"
                minH="44px"
              />
              <MenuList minW="12rem">
                <MenuItem
                  icon={<TablerEdit size={16} aria-hidden="true" />}
                  onClick={onEditPatient}
                >
                  Edit Patient
                </MenuItem>
                {canDeletePatient && (
                  <MenuItem
                    icon={<TablerTrash size={16} aria-hidden="true" />}
                    onClick={onDeletePatient}
                  >
                    Delete Patient
                  </MenuItem>
                )}
              </MenuList>
            </Menu>
          )}
          {!inDetail && (
            <Button
              type="button"
              aria-label="Clear patient"
              variant="outline"
              size="md"
              leftIcon={<TablerX size={16} aria-hidden="true" />}
              onClick={onClose}
              whiteSpace="nowrap"
              data-testid="planner-patient-clear-button"
            >
              Clear
            </Button>
          )}
        </HStack>
      </Flex>
    </Box>
  );
};

const PlannerEncounterStartBox = ({
  patientItem,
  seedEvent,
  scheduledEncounterEvent,
  fallbackLanguage = '',
  onScheduled,
}: Record<string, any>) => {
  const styles = usePlannerStyles();
  const {
    alreadyWithPatient,
    setAlreadyWithPatient,
    phoneJoinMode,
    setPhoneJoinMode,
    createSessionValidation,
    dialect,
    dialectOptions,
    dialectSearch,
    dialectTypeaheadRef,
    filteredDialectOptions,
    filteredLanguageOptions,
    handleClearDialect,
    handleClearLanguage,
    handleDialectSearchChange,
    handleInterpreterToggle,
    handleLocationChange,
    handleLanguageFocus,
    handleLanguageSearchChange,
    handleSelectDialectOption,
    handleSelectLanguageOption,
    handleScheduleDateChange,
    handleScheduleSubmit,
    handleScheduleTimeChange,
    handleStart,
    canShowSchedule,
    interpreterEnabled,
    isDialectTypeaheadOpen,
    isLanguageTypeaheadOpen,
    isScheduling,
    isStarting,
    language,
    languageSearch,
    languageTypeaheadRef,
    locationType,
    openScheduleModal,
    patientPhone,
    providerPhone,
    resetLaunchState,
    resetLanguageFields,
    scheduleDisclosure,
    scheduledDateKey,
    scheduledTimeKey,
    seedLanguageSelection,
    setIsDialectTypeaheadOpen,
    setIsLanguageTypeaheadOpen,
    setPatientPhone,
    setProviderPhone,
    setVideoCallLink,
    shouldShowDialectTypeahead,
    shouldShowLanguageSearchStatus,
    userTimezoneLabel,
    visibleErrors,
    visibleScheduleError,
    videoCallLink,
    dateOptions,
  } = useEncounterLauncher({
    patientItem,
    patientSkipped: false,
    fromPlanner: true,
    onScheduled,
  });

  const composerSeed = useMemo(
    () =>
      buildEncounterComposerSeedFromEvent(scheduledEncounterEvent || seedEvent),
    [scheduledEncounterEvent, seedEvent],
  );
  const patientSeedLanguage = patientLanguageFromItem(patientItem);
  const patientSeedDialect = patientItem?.localPatient?.preferred_dialect || '';
  const patientSeedPhone = patientPhoneFromItem(patientItem);
  const interpreterLanguageSeed = useMemo(() => {
    const seedLanguage =
      patientSeedLanguage || composerSeed?.language || fallbackLanguage;
    const seedDialect = patientSeedLanguage
      ? patientSeedDialect
      : composerSeed?.dialect || patientSeedDialect;

    return {
      dialect: seedDialect || '',
      language: seedLanguage || '',
    };
  }, [
    composerSeed?.dialect,
    composerSeed?.language,
    fallbackLanguage,
    patientSeedDialect,
    patientSeedLanguage,
  ]);

  // Reset the launch state (location, phone numbers, "on my phone" join mode,
  // etc.) only when the selected patient or seed event actually changes. This
  // must NOT depend on the resolved language/dialect, nor on `composerSeed.key`:
  // EHR-linked patients hydrate their demographics and history asynchronously,
  // and `composerSeed` is derived from `scheduledEncounterEvent`, which resolves
  // a matching appointment for the same patient out of `sections` after the fact
  // — flipping its id mid-edit. Keying the reset on those would re-run it while
  // the provider is filling the form and silently wipe out in-progress phone
  // selections (e.g. snapping the typed patient number back to the chart number,
  // or "On my phone" back to "On this device"). Key only on stable identity:
  // the patient and the actually-selected event.
  useEffect(() => {
    // The interpreter default (on for a non-English language the provider
    // doesn't speak, off otherwise) is computed centrally in
    // useEncounterLauncher, so this reset only clears the location/phone state.
    resetLaunchState(patientPhoneFromItem(patientItem));
  }, [patientItem?.id, seedEvent?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Scheduled EHR appointments resolve to a patient that has no language on
    // the appointment/EHR row, so fall back to the language from this patient's
    // own encounter history — that's how a Spanish-speaking scheduled patient
    // still seeds the interpreter the same way the search path does. Keep the
    // radio default off; the seed is applied when the provider opts in.
    if (
      interpreterLanguageSeed.language &&
      isNonEnglish(interpreterLanguageSeed.language)
    ) {
      seedLanguageSelection(
        interpreterLanguageSeed.language,
        interpreterLanguageSeed.dialect,
      );
    } else {
      resetLanguageFields();
    }
  }, [
    composerSeed?.key,
    interpreterLanguageSeed.dialect,
    interpreterLanguageSeed.language,
    patientItem?.id,
    patientSeedPhone,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePatientInterpreterToggle = useCallback(
    (checked: any) => {
      handleInterpreterToggle(checked);
      if (
        checked &&
        interpreterLanguageSeed.language &&
        isNonEnglish(interpreterLanguageSeed.language)
      ) {
        seedLanguageSelection(
          interpreterLanguageSeed.language,
          interpreterLanguageSeed.dialect,
        );
      }
    },
    [
      handleInterpreterToggle,
      interpreterLanguageSeed.dialect,
      interpreterLanguageSeed.language,
      seedLanguageSelection,
    ],
  );

  return (
    <Box
      as="section"
      bg={styles.surfaceBg}
      borderWidth="1px"
      borderColor={styles.borderColor}
      borderRadius="xl"
      boxShadow={styles.cardShadow}
      data-testid="planner-encounter-start-box"
    >
      <Box p={{base: 4, md: 5}}>
        <Text as="h2" fontSize="lg" fontWeight="semibold" lineHeight="1.3">
          New Encounter
        </Text>

        <VStack align="stretch" spacing={4} mt={5}>
          <InterpreterSection
            dialect={dialect}
            dialectOptions={dialectOptions}
            dialectSearch={dialectSearch}
            dialectTypeaheadRef={dialectTypeaheadRef}
            filteredDialectOptions={filteredDialectOptions}
            filteredLanguageOptions={filteredLanguageOptions}
            handleClearDialect={handleClearDialect}
            handleClearLanguage={handleClearLanguage}
            handleDialectSearchChange={handleDialectSearchChange}
            handleInterpreterToggle={handlePatientInterpreterToggle}
            handleLanguageFocus={handleLanguageFocus}
            handleLanguageSearchChange={handleLanguageSearchChange}
            handleSelectDialectOption={handleSelectDialectOption}
            handleSelectLanguageOption={handleSelectLanguageOption}
            interpreterEnabled={interpreterEnabled}
            isDialectTypeaheadOpen={isDialectTypeaheadOpen}
            isLanguageTypeaheadOpen={isLanguageTypeaheadOpen}
            language={language}
            languageSearch={languageSearch}
            languageTypeaheadRef={languageTypeaheadRef}
            setIsDialectTypeaheadOpen={setIsDialectTypeaheadOpen}
            setIsLanguageTypeaheadOpen={setIsLanguageTypeaheadOpen}
            shouldShowDialectTypeahead={shouldShowDialectTypeahead}
            shouldShowLanguageSearchStatus={shouldShowLanguageSearchStatus}
            visibleErrors={visibleErrors}
          />
          <LocationSection
            alreadyWithPatient={alreadyWithPatient}
            handleLocationChange={handleLocationChange}
            interpreterEnabled={interpreterEnabled}
            locationType={locationType}
            patientPhone={patientPhone}
            phoneJoinMode={phoneJoinMode}
            providerPhone={providerPhone}
            setAlreadyWithPatient={setAlreadyWithPatient}
            setPatientPhone={setPatientPhone}
            setPhoneJoinMode={setPhoneJoinMode}
            setProviderPhone={setProviderPhone}
            setVideoCallLink={setVideoCallLink}
            visibleErrors={visibleErrors}
            videoCallLink={videoCallLink}
          />
        </VStack>
      </Box>

      <CreateSessionFooter
        canSchedule={canShowSchedule}
        canStart={createSessionValidation.isValid}
        isStarting={isStarting}
        onSchedule={openScheduleModal}
        onStart={handleStart}
      />

      <CreateSessionScheduleModal
        dateOptions={dateOptions}
        isOpen={scheduleDisclosure.isOpen}
        isSubmitting={isScheduling}
        onClose={scheduleDisclosure.onClose}
        onDateChange={handleScheduleDateChange}
        onSubmit={handleScheduleSubmit}
        onTimeChange={handleScheduleTimeChange}
        scheduledDateKey={scheduledDateKey}
        scheduledTimeKey={scheduledTimeKey}
        userTimezoneLabel={userTimezoneLabel}
        visibleScheduleError={visibleScheduleError}
      />
    </Box>
  );
};

const encounterTimestamp = (encounter: any) =>
  encounter?.end_time || encounter?.start_time;

const encounterLanguage = (encounter: any) => {
  const directLanguage = clean(
    encounter?.foreign_language ||
      encounter?.foreignLanguage ||
      encounter?.language,
  );
  if (plannerLanguageMarker(directLanguage)) return directLanguage;

  const conversations = Array.isArray(encounter?.conversations)
    ? encounter.conversations
    : [];
  return (
    conversations
      .map((conversation: any) =>
        clean(
          conversation?.foreign_language ||
            conversation?.foreignLanguage ||
            conversation?.language,
        ),
      )
      .find((language: any) => plannerLanguageMarker(language)) || ''
  );
};

const encounterLanguageLabel = (encounter: any) => {
  const language = encounterLanguage(encounter);
  if (!language || !plannerLanguageMarker(language)) return '';
  return displayLanguageName(language);
};

const encounterElapsedLabel = (encounter: any) => {
  const minutes = Math.max(Number(encounter?.total_minutes) || 0, 1);
  return `${minutes} min`;
};

// "9:00 AM – 9:12 AM" when we have both ends, otherwise whichever single time
// we know. The window (not just the start) gives the row real shape — providers
// can scan how long a visit ran without opening it.
const encounterTimeRangeLabel = (encounter: any) => {
  const start = formatPlannerSidebarTime(encounter?.start_time);
  const end = formatPlannerSidebarTime(encounter?.end_time);
  if (start && end && start !== end) return `${start} – ${end}`;
  return start || end || '';
};

// Who ran the encounter. A solo session resolves to the one provider (name →
// email); a multi-provider encounter collapses to a count so the caption stays
// short. Empty when we can't attribute it (legacy rows with no contributor).
const encounterProviderLabel = (encounter: any) => {
  const contributors = Array.isArray(encounter?.contributors)
    ? encounter.contributors
    : [];
  if (contributors.length === 0) return '';
  if (contributors.length > 1) return `${contributors.length} providers`;
  const [only] = contributors;
  return clean(only?.provider_name) || clean(only?.userEmail) || '';
};

// The flag + language pill shown on the row's top line. Null for English /
// scribe-only encounters so the pill collapses instead of rendering a fallback.
const encounterLanguageChip = (encounter: any) => {
  const language = encounterLanguageLabel(encounter);
  if (!language) return null;
  return {flag: languageFlag(language), label: language};
};

// Duration + provider only. The session "type" is conveyed by the language
// pill that now leads this row (a foreign-language pill implies an interpreter
// session), so spelling out "Interpreter"/"Scribe" here is redundant noise.
const encounterHistoryMetadataLabel = (encounter: any, elapsedLabel: string) =>
  [elapsedLabel, encounterProviderLabel(encounter)]
    .map(clean)
    .filter(Boolean)
    .join(' • ');

// Empty when there's no summary yet, so the card can collapse the summary line
// entirely (matching the recent list) rather than reserving space for a
// placeholder.
const encounterSummaryLabel = (encounter: any) => clean(encounter?.summary);

// History encounter card.
//
// Gives the row a clear visual hierarchy so a single glance carries far more
// than the old "time + one muted line":
//   - A bold time *window* (start – end) anchors the row.
//   - The encounter summary as a browser-Recent-style prominent line.
//   - A muted "duration • modality • provider" caption underneath.
// The whole card opens the encounter detail view; the chevron is a visual cue,
// not a separate action surface.
const PlannerHistoryEncounterCard = ({
  time,
  timeRange,
  languageChip,
  metadataLabel,
  summary,
  ariaLabel,
  isGrouped = false,
  isFirstInGroup = false,
  isLastInGroup = false,
  isHovered = false,
  showBottomDivider = false,
  isRemoving = false,
  onHoverEnd,
  onHoverStart,
  onRemove,
  onView,
  styles,
}: Record<string, any>) => {
  const groupedEdgeRadius = 'calc(var(--chakra-radii-xl) - 1px)';
  const hasBottomDivider = isGrouped && showBottomDivider;
  const bottomDividerStyles = hasBottomDivider
    ? {
        content: '""',
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        borderBottom: '1px solid',
        borderColor: styles.rowBorderColor,
        pointerEvents: 'none',
      }
    : undefined;
  const focusWithinStyles = {
    ...styles.focusOutlineStyles,
    borderColor: styles.listRowSelectedBorderColor,
    outlineOffset: '-2px',
    ...(isGrouped ? {zIndex: 1} : {}),
  };
  const handleKeyDown = (event: any) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onView();
    }
  };

  return (
    <Grid
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      data-hovered={isHovered ? 'true' : 'false'}
      data-show-bottom-divider={hasBottomDivider ? 'true' : 'false'}
      data-testid="planner-history-encounter-card"
      templateColumns="minmax(0, 1fr) 44px"
      alignItems="stretch"
      appearance="none"
      bg={styles.rowBg}
      borderWidth={isGrouped ? 0 : '1px'}
      borderColor={styles.rowBorderColor}
      borderRadius={isGrouped ? 0 : 'xl'}
      borderTopLeftRadius={
        isGrouped && isFirstInGroup ? groupedEdgeRadius : undefined
      }
      borderTopRightRadius={
        isGrouped && isFirstInGroup ? groupedEdgeRadius : undefined
      }
      borderBottomLeftRadius={
        isGrouped && isLastInGroup ? groupedEdgeRadius : undefined
      }
      borderBottomRightRadius={
        isGrouped && isLastInGroup ? groupedEdgeRadius : undefined
      }
      boxShadow={isGrouped ? 'none' : '0 1px 2px rgba(23, 23, 23, 0.04)'}
      color={styles.rowActionColor}
      cursor="pointer"
      fontFamily="inherit"
      overflow="hidden"
      position="relative"
      textAlign="left"
      transition={PLANNER_POINTER_HOVER_TRANSITION}
      w="100%"
      onClick={onView}
      onKeyDown={handleKeyDown}
      onMouseEnter={onHoverStart}
      onMouseLeave={onHoverEnd}
      _after={bottomDividerStyles}
      _hover={{
        bg: styles.listRowSelectedBg,
        borderRadius: PLANNER_OPALITE_SELECTION_RADIUS,
        borderColor: isGrouped
          ? styles.rowBorderColor
          : styles.listRowSelectedBorderColor,
      }}
      _focusVisible={focusWithinStyles}
    >
      <Box data-testid="planner-recent-encounter-row" px={4} py={3} minW={0}>
        <Text
          data-testid="planner-encounter-time"
          fontSize="md"
          lineHeight="1.2"
          color={styles.rowActionColor}
          noOfLines={1}
          mb={1.5}
          sx={{
            fontVariantNumeric: 'tabular-nums',
            fontWeight: '600 !important',
          }}
        >
          {timeRange || time}
        </Text>

        {summary && (
          <Text
            data-planner-encounter-summary-font-size="md"
            data-planner-encounter-summary-prominent="true"
            fontSize="md"
            fontWeight="medium"
            lineHeight="1.4"
            color={styles.rowActionColor}
            noOfLines={2}
          >
            {summary}
          </Text>
        )}

        {/* Bottom line: the language pill leads (when the visit used one), then
            the muted "<duration> • <provider>" caption. */}
        {(languageChip || metadataLabel) && (
          <Flex
            align="center"
            gap={2}
            rowGap={1}
            minW={0}
            mt={summary ? 1.5 : 0}
            flexWrap="wrap"
          >
            {languageChip && (
              <MetaPill
                data-testid="planner-encounter-language"
                gap={1}
                flexShrink={0}
                fontSize="sm"
                bg={styles.metaPillBg}
                color={styles.metaPillColor}
                borderColor={styles.metaPillBorderColor}
                maxW="11rem"
              >
                <Box as="span" aria-hidden="true">
                  {languageChip.flag}
                </Box>
                <Box as="span">{languageChip.label}</Box>
              </MetaPill>
            )}

            {metadataLabel && (
              <Text
                data-planner-encounter-metadata-font-size="sm"
                data-testid="planner-encounter-metadata"
                color={styles.metaTextColor}
                fontSize="sm"
                fontWeight="normal"
                lineHeight="1.35"
                noOfLines={1}
                minW={0}
              >
                {metadataLabel}
              </Text>
            )}
          </Flex>
        )}
      </Box>
      <Flex
        data-testid="planner-history-row-actions"
        align="center"
        justify="center"
      >
        {onRemove ? (
          <Menu placement="bottom-end" isLazy>
            <MenuButton
              as={IconButton}
              type="button"
              aria-label="Encounter actions"
              icon={<TablerDotsVertical size={18} aria-hidden="true" />}
              variant="ghost"
              size="sm"
              minW="36px"
              h="36px"
              borderRadius="md"
              color={styles.captionTextColor}
              isLoading={isRemoving}
              // The whole row is itself a button that opens the encounter;
              // stop pointer + keyboard activation here so reaching for the
              // menu never also drills into the detail view.
              onClick={(event: any) => event.stopPropagation()}
              onKeyDown={(event: any) => event.stopPropagation()}
              _hover={{
                bg: styles.listRowSelectedBg,
                color: styles.rowActionColor,
              }}
              _active={{bg: styles.listRowSelectedBg}}
            />
            <MenuList minW="10rem">
              <MenuItem
                icon={<TablerTrash size={16} aria-hidden="true" />}
                onClick={(event: any) => {
                  event.stopPropagation();
                  onRemove();
                }}
              >
                Remove
              </MenuItem>
            </MenuList>
          </Menu>
        ) : (
          <Box
            color={styles.captionTextColor}
            pointerEvents="none"
            aria-hidden="true"
          >
            <TablerChevronRight size={18} focusable="false" />
          </Box>
        )}
      </Flex>
    </Grid>
  );
};

// Soft-delete an encounter from the patient timeline. Real encounters route
// through `softDeleteEncounter` (which also flips their member conversations);
// synthetic/implicit encounters have no Encounter doc, so we archive their
// underlying conversation(s) directly the same way the detail panel does.
async function removeEncounterView(encounter: any) {
  const encounterId = encounter?.id;
  if (
    encounterId &&
    !encounter?.isSynthetic &&
    !isImplicitEncounterId(encounterId)
  ) {
    await softDeleteEncounter(encounterId);
    return;
  }
  const conversationIds = (
    Array.isArray(encounter?.conversations) ? encounter.conversations : []
  )
    .map((conversation: any) => conversation?.id)
    .filter(Boolean);
  await Promise.all(
    conversationIds.map((id: string) => patientService.archiveSession(id)),
  );
}

const PlannerRecentEncounters = ({
  encounterViews,
  isLoading,
  error,
  currentUserId,
  onSelectEncounter,
  onEncounterRemoved,
}: Record<string, any>) => {
  const styles = usePlannerStyles();
  const toast = useToast();
  const [hoveredHistoryRowKey, setHoveredHistoryRowKey] = useState<any>(null);
  const [collapsedDateGroups, setCollapsedDateGroups] = useState(
    () => new Set(),
  );
  const [removingKeys, setRemovingKeys] = useState<Set<string>>(
    () => new Set(),
  );
  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(() => new Set());

  const handleSelectEncounter = useCallback(
    (encounter: any) => {
      const target = pickRepresentativeConversation(encounter, currentUserId);
      onSelectEncounter(target || encounter);
    },
    [currentUserId, onSelectEncounter],
  );

  const handleRemoveEncounter = useCallback(
    async (key: string, encounter: any) => {
      if (!encounter || removingKeys.has(key)) return;
      const confirmed =
        typeof window !== 'undefined'
          ? window.confirm(
              'Remove this encounter from the history? You can restore it anytime from the Removed sessions section.',
            )
          : true;
      if (!confirmed) return;
      setRemovingKeys(prev => new Set(prev).add(key));
      try {
        await removeEncounterView(encounter);
        // Hide it instantly; the parent refresh below refetches the timeline
        // so the row stays gone once Firestore propagates.
        setHiddenKeys(prev => new Set(prev).add(key));
        toast({
          title: 'Encounter removed',
          description: 'Moved to Removed sessions',
          status: 'info',
          duration: 3500,
          isClosable: true,
        });
        onEncounterRemoved?.();
      } catch (err: any) {
        console.error('Failed to remove encounter from planner history:', err);
        toast({
          title: 'Could not remove encounter',
          status: 'error',
          duration: 3000,
        });
      } finally {
        setRemovingKeys(prev => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [onEncounterRemoved, removingKeys, toast],
  );

  const handleDateGroupToggle = useCallback((groupKey: any) => {
    setCollapsedDateGroups(previous => {
      const next = new Set(previous);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  }, []);
  const encounterRows = useMemo(
    () =>
      encounterViews.map((encounter: any) => {
        const timestamp = encounterTimestamp(encounter);
        const language = encounterLanguageLabel(encounter);
        const elapsedLabel = encounterElapsedLabel(encounter);
        const metadataLabel = encounterHistoryMetadataLabel(
          encounter,
          elapsedLabel,
        );
        const summary = encounterSummaryLabel(encounter);
        const dateLabel = formatEncounterDate(timestamp);
        const languageChip = encounterLanguageChip(encounter);
        return {
          key: encounter?.id || 'unknown-encounter',
          encounter,
          timestamp,
          time: formatPlannerSidebarTime(encounter?.start_time),
          timeRange: encounterTimeRangeLabel(encounter),
          language,
          languageChip,
          elapsedLabel,
          metadataLabel,
          summary,
          ariaLabel:
            `View encounter ${[dateLabel, language, summary].filter(Boolean).join(' ')}`.trim(),
        };
      }),
    [encounterViews],
  );

  const visibleEncounterRows = useMemo(
    () => encounterRows.filter((row: any) => !hiddenKeys.has(row.key)),
    [encounterRows, hiddenKeys],
  );

  const encounterDateGroups = useMemo(
    () =>
      groupPlannerRowsByDate(
        visibleEncounterRows,
        (row: any) => row?.timestamp,
        new Date(),
        {dateGrouping: 'past', sortDirection: 'desc'},
      ),
    [visibleEncounterRows],
  );

  if (isLoading) {
    return (
      <Flex justify="center" py={8} aria-live="polite" aria-busy="true">
        <Spinner size="md" color={styles.accentColor} />
      </Flex>
    );
  }

  if (error) {
    return (
      <Text fontSize="md" fontWeight="normal" py={3}>
        Could not load past encounters.
      </Text>
    );
  }

  if (encounterViews.length === 0) {
    return (
      <Text fontSize="md" fontWeight="normal" py={3}>
        No past encounters yet.
      </Text>
    );
  }

  return (
    <>
      <VStack align="stretch" spacing={4}>
        {encounterDateGroups.map((group: any) => {
          const isCollapsed = collapsedDateGroups.has(group.key);
          const contentId = `planner-history-${group.key}-date-group`;
          const hoveredRowIndex = group.rows.findIndex(
            ({item}: any) => item.key === hoveredHistoryRowKey,
          );

          return (
            <Box key={group.key}>
              <PlannerNavigationSectionHeader
                bg={styles.pageBg}
                contentId={contentId}
                count={group.rows.length}
                isCollapsed={isCollapsed}
                isCollapsible
                label={group.label}
                onToggle={() => handleDateGroupToggle(group.key)}
                styles={styles}
                labelTestId="planner-date-group-label"
                testId="planner-date-group-header"
              />
              {!isCollapsed && (
                <Box
                  id={contentId}
                  data-testid="planner-history-date-group-card"
                  bg={styles.rowBg}
                  borderWidth="1px"
                  borderColor={styles.rowBorderColor}
                  borderRadius="xl"
                  boxShadow="0 1px 2px rgba(23, 23, 23, 0.04)"
                  overflow="hidden"
                  mt={1.5}
                >
                  {group.rows.map(({item}: any, index: number) => {
                    const isHovered = item.key === hoveredHistoryRowKey;
                    const isNextToHovered =
                      hoveredRowIndex >= 0 &&
                      (index === hoveredRowIndex ||
                        index === hoveredRowIndex - 1);
                    const showBottomDivider =
                      index < group.rows.length - 1 && !isNextToHovered;

                    return (
                      <PlannerHistoryEncounterCard
                        key={item.key}
                        time={item.time}
                        timeRange={item.timeRange}
                        languageChip={item.languageChip}
                        metadataLabel={item.metadataLabel}
                        summary={item.summary}
                        ariaLabel={item.ariaLabel}
                        isGrouped
                        isFirstInGroup={index === 0}
                        isHovered={isHovered}
                        isLastInGroup={index === group.rows.length - 1}
                        showBottomDivider={showBottomDivider}
                        isRemoving={removingKeys.has(item.key)}
                        onHoverStart={() => setHoveredHistoryRowKey(item.key)}
                        onHoverEnd={() =>
                          setHoveredHistoryRowKey((current: any) =>
                            current === item.key ? null : current,
                          )
                        }
                        onRemove={() =>
                          handleRemoveEncounter(item.key, item.encounter)
                        }
                        onView={() => handleSelectEncounter(item.encounter)}
                        styles={styles}
                      />
                    );
                  })}
                </Box>
              )}
            </Box>
          );
        })}
      </VStack>
    </>
  );
};

const PlannerPatientPanel = ({
  event,
  patient,
  sections,
  selectedSession,
  onClose,
  onCloseSessionDetail,
  onSelectSession,
  onPatientUpdated,
  onPatientDeleted,
  onScheduled,
  onSessionMutation,
  onDocumentCall,
}: Record<string, any>) => {
  const {userData} = useUser();
  const [patientOverride, setPatientOverride] = useState<any>(null);
  useEffect(() => {
    setPatientOverride(null);
  }, [patient?.id]);
  const currentPatient = patientOverride || patient;
  const currentPatientName = useMemo(() => {
    if (!currentPatient) return '';
    return patientDisplayNameFromRecord(currentPatient);
  }, [currentPatient]);
  const currentEvent = useMemo(() => {
    if (!event || !currentPatient) return event;
    const patientName = currentPatientName || event.patientName;
    return {
      ...event,
      patient: currentPatient,
      patientName,
      language: currentPatient.language || event.language,
    };
  }, [currentPatient, currentPatientName, event]);
  const patientItem = useMemo(
    () => plannerPatientToPatientItem(currentPatient),
    [currentPatient],
  );
  const scheduledEncounterEvent = useMemo(
    () =>
      findScheduledEncounterEvent({
        selectedEvent: currentEvent,
        selectedPatient: currentPatient,
        sections,
      }),
    [currentEvent, currentPatient, sections],
  );
  const [refreshKey, setRefreshKey] = useState(0);
  const prefersReducedMotion = useReducedMotion();
  const {encounterViews, error, isLoading} = usePlannerPatientEncounters({
    patientItem,
    refreshKey,
  });
  // Most-recent non-English language from this patient's history, used to seed
  // the New Encounter interpreter when the selected event itself carries none
  // (e.g. a scheduled EHR appointment with no language on the row).
  const historySeedLanguage = useMemo(() => {
    for (const encounter of encounterViews) {
      const language = encounterLanguage(encounter);
      if (language) return language;
    }
    return '';
  }, [encounterViews]);
  const styles = usePlannerStyles();
  const scrollbarSx = useScrollbarSx();

  const handleSessionMutation = useCallback(() => {
    setRefreshKey(key => key + 1);
    onSessionMutation?.();
  }, [onSessionMutation]);
  const handlePatientUpdated = useCallback(
    (updates: any) => {
      const nextPatient = mergePlannerPatientUpdate(currentPatient, updates);
      setPatientOverride(nextPatient);
      onPatientUpdated?.(nextPatient);
    },
    [currentPatient, onPatientUpdated],
  );
  const handlePatientDeleted = useCallback(
    (patientId: any, deletedPatient: any) => {
      // Prune the just-deleted patient from the roster immediately so it can't
      // be re-selected and re-deleted (the duplicate-delete → 404 bug), then
      // close the panel and refresh the schedule.
      onPatientDeleted?.(patientId, deletedPatient);
      onClose?.();
      onScheduled?.();
    },
    [onClose, onPatientDeleted, onScheduled],
  );
  const {
    canDeletePatient,
    canEditPatient,
    cancelDeleteRef,
    closeDeleteDialog,
    closeEditModal,
    deleteDialog,
    deleting,
    editModal,
    formData,
    handleDeletePatient,
    handleUpdatePatient,
    openDeleteDialog,
    openEditPatient,
    saving,
    setFormData,
  } = usePatientCrud({
    patient: currentPatient,
    onDeleted: handlePatientDeleted,
    onUpdated: handlePatientUpdated,
  });

  if (!currentEvent || !currentPatient || !patientItem) {
    return null;
  }

  return (
    <Box
      h="100%"
      minH={0}
      display="flex"
      flexDirection="column"
      data-testid="planner-patient-workspace"
    >
      <PlannerPatientHeader
        event={currentEvent}
        canDeletePatient={canDeletePatient}
        canEditPatient={canEditPatient}
        onBack={selectedSession ? onCloseSessionDetail : null}
        onClose={onClose}
        onDeletePatient={openDeleteDialog}
        onEditPatient={openEditPatient}
      />

      <Box position="relative" flex={1} minH={0} overflow="hidden">
        <Box
          h="100%"
          minH={0}
          overflowY="auto"
          // A full step of padding between the cards and the scrollbar lane —
          // the styled (non-overlay) scrollbar occupies real layout space, so
          // without this the card borders sit flush against the track.
          pr={3}
          visibility={selectedSession ? 'hidden' : 'visible'}
          pointerEvents={selectedSession ? 'none' : 'auto'}
          aria-hidden={selectedSession ? 'true' : undefined}
          sx={scrollbarSx}
        >
          <PlannerEncounterStartBox
            patientItem={patientItem}
            seedEvent={currentEvent}
            scheduledEncounterEvent={scheduledEncounterEvent}
            fallbackLanguage={historySeedLanguage}
            onScheduled={onScheduled}
          />

          <Box
            as="section"
            mt={5}
            pb={8}
            data-testid="planner-recent-encounters"
          >
            <Text
              as="h2"
              fontSize="lg"
              fontWeight="semibold"
              lineHeight="1.3"
              mb={2}
            >
              History
            </Text>
            <PlannerRecentEncounters
              encounterViews={encounterViews}
              isLoading={isLoading}
              error={error}
              currentUserId={userData?.userId}
              onSelectEncounter={onSelectSession}
              onEncounterRemoved={handleSessionMutation}
            />
          </Box>
        </Box>

        <AnimatePresence>
          {selectedSession && (
            <MotionBox
              key="planner-patient-session-overlay"
              position="absolute"
              inset={0}
              pr={1}
              bg={styles.pageBg}
              data-testid="planner-encounter-detail-overlay"
              variants={
                prefersReducedMotion
                  ? undefined
                  : ENCOUNTER_DETAIL_OVERLAY_VARIANTS
              }
              initial={prefersReducedMotion ? false : 'hidden'}
              animate={prefersReducedMotion ? undefined : 'visible'}
              exit={prefersReducedMotion ? undefined : 'exit'}
            >
              <Box h="100%" minH={0} display="flex" flexDirection="column">
                <Box flex={1} minH={0}>
                  <Suspense fallback={<ViewLoader label="Loading session…" />}>
                    <SessionDetailPanel
                      session={selectedSession}
                      patientItem={patientItem}
                      fromPlanner
                      onSessionRemoved={handleSessionMutation}
                      onSessionRestored={handleSessionMutation}
                      onSessionPatientChanged={handleSessionMutation}
                      onDocumentCall={onDocumentCall}
                    />
                  </Suspense>
                </Box>
              </Box>
            </MotionBox>
          )}
        </AnimatePresence>
      </Box>
      <PatientFormModal
        isOpen={editModal.isOpen}
        onClose={closeEditModal}
        title="Edit Patient"
        formData={formData}
        setFormData={setFormData}
        onSave={handleUpdatePatient}
        saving={saving}
        saveLabel="Save Changes"
      />
      <AlertDialog
        isOpen={deleteDialog.isOpen}
        leastDestructiveRef={cancelDeleteRef}
        onClose={closeDeleteDialog}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="xl" mx={4}>
            <AlertDialogHeader fontSize="lg" fontWeight="normal">
              Delete patient
            </AlertDialogHeader>
            <AlertDialogBody>
              Delete {currentPatientName || 'this patient'}? This cannot be
              undone. Their sessions will stay but will not be linked to this
              patient anymore.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelDeleteRef}
                onClick={closeDeleteDialog}
                variant="ghost"
              >
                Cancel
              </Button>
              <Button
                colorScheme="gray"
                onClick={handleDeletePatient}
                ml={3}
                isLoading={deleting}
                loadingText="Deleting..."
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default PlannerPatientPanel;
